import { useState, useEffect, useMemo } from 'react';
import { X, FileText, Loader2, Search, Check } from 'lucide-react';
import { ACCENT as SECTION_COLOR } from '../lib/uiTokens';
import { STAV_CONFIG } from './FinanceDokladyInbox';

const prvniVMesici = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const dnes = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/**
 * Report dokladů → PDF. Období dle DUZP + výběr stavů + dodavatelů (prázdný výběr
 * = vše). „Generovat PDF" otevře GET /finance/doklady/report v novém okně
 * (session cookie se přenese, žádné blob řešení).
 */
export default function DokladReportModal({ cardStyle, onClose }) {
    const [od, setOd] = useState(prvniVMesici);
    const [doDatum, setDoDatum] = useState(dnes);
    const [stavy, setStavy] = useState(() => new Set());
    const [dodavatele, setDodavatele] = useState([]);
    const [vybraniDod, setVybraniDod] = useState(() => new Set());
    const [hledej, setHledej] = useState('');
    const [nacitam, setNacitam] = useState(true);
    const [serazeni, setSerazeni] = useState('duzp');
    const [mezisoucty, setMezisoucty] = useState(false);

    useEffect(() => {
        fetch('/api/dodavatele?aktivni=1')
            .then(r => r.json())
            .then(data => { setDodavatele(Array.isArray(data) ? data : []); setNacitam(false); })
            .catch(() => setNacitam(false));
    }, []);

    const toggleStav = (k) => setStavy(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
    const toggleDod = (id) => setVybraniDod(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const filtrovaniDod = useMemo(() => {
        const q = hledej.trim().toLowerCase();
        return q ? dodavatele.filter(d => d.nazev?.toLowerCase().includes(q)) : dodavatele;
    }, [dodavatele, hledej]);

    const generovat = () => {
        const params = new URLSearchParams();
        params.set('od', od);
        params.set('do', doDatum);
        stavy.forEach(s => params.append('stav[]', s));
        vybraniDod.forEach(id => params.append('dodavatel_id[]', id));
        params.set('serazeni', serazeni);
        if (mezisoucty && serazeni !== 'castka') params.set('mezisoucty', '1');
        window.open(`/api/finance/doklady/report?${params.toString()}`, '_blank');
    };

    const platne = od && doDatum && od <= doDatum;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="w-full max-w-lg max-h-[88vh] flex flex-col rounded-2xl overflow-hidden" style={{ ...cardStyle }} onClick={(e) => e.stopPropagation()}>

                {/* Hlavička */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-2">
                        <FileText size={18} style={{ color: SECTION_COLOR }} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Report dokladů (PDF)</h3>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10">
                        <X size={18} style={{ color: 'var(--text-tertiary)' }} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {/* Období dle DUZP */}
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Období (dle DUZP)</div>
                        <div className="flex items-center gap-2">
                            <input type="date" value={od} onChange={e => setOd(e.target.value)}
                                className="flex-1 rounded-lg px-2 py-1.5 text-sm" style={{ ...cardStyle, color: 'var(--text-primary)' }} />
                            <span style={{ color: 'var(--text-tertiary)' }}>–</span>
                            <input type="date" value={doDatum} onChange={e => setDoDatum(e.target.value)}
                                className="flex-1 rounded-lg px-2 py-1.5 text-sm" style={{ ...cardStyle, color: 'var(--text-primary)' }} />
                        </div>
                    </div>

                    {/* Řazení + mezisoučty */}
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Seřadit dle</div>
                        <div className="flex flex-wrap gap-1.5">
                            {[['duzp', 'DUZP (datum)'], ['dodavatel', 'Dodavatel'], ['stav', 'Stav'], ['castka', 'Částka']].map(([k, label]) => {
                                const on = serazeni === k;
                                return (
                                    <button key={k} onClick={() => setSerazeni(k)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium"
                                        style={on
                                            ? { background: SECTION_COLOR, color: '#fff', border: `1px solid ${SECTION_COLOR}` }
                                            : { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                                        {on && <Check size={12} />} {label}
                                    </button>
                                );
                            })}
                        </div>
                        <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer" style={{ color: serazeni === 'castka' ? 'var(--text-tertiary)' : 'var(--text-secondary)' }}>
                            <input type="checkbox" checked={mezisoucty && serazeni !== 'castka'} disabled={serazeni === 'castka'}
                                onChange={e => setMezisoucty(e.target.checked)} style={{ accentColor: SECTION_COLOR }} />
                            Mezisoučty podle klíče řazení
                            {serazeni === 'duzp' && <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>(po měsících)</span>}
                            {serazeni === 'castka' && <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>(u částky nelze)</span>}
                        </label>
                    </div>

                    {/* Stavy */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Stavy</div>
                            <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{stavy.size === 0 ? 'vše' : `${stavy.size} vybráno`}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {Object.entries(STAV_CONFIG).map(([k, cfg]) => {
                                const on = stavy.has(k);
                                return (
                                    <button key={k} onClick={() => toggleStav(k)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium"
                                        style={on
                                            ? { background: cfg.color, color: '#fff', border: `1px solid ${cfg.color}` }
                                            : { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                                        {on && <Check size={12} />} {cfg.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Dodavatelé */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Dodavatelé</div>
                            <div className="flex items-center gap-2 text-[11px]">
                                <span style={{ color: 'var(--text-tertiary)' }}>{vybraniDod.size === 0 ? 'všichni' : `${vybraniDod.size} vybráno`}</span>
                                {vybraniDod.size > 0 && (
                                    <button onClick={() => setVybraniDod(new Set())} style={{ color: SECTION_COLOR }}>Zrušit</button>
                                )}
                            </div>
                        </div>
                        <div className="relative mb-2">
                            <Search size={13} style={{ position: 'absolute', left: 8, top: 9, color: 'var(--text-tertiary)' }} />
                            <input value={hledej} onChange={e => setHledej(e.target.value)} placeholder="Hledat dodavatele…"
                                className="w-full rounded-lg pl-7 pr-2 py-1.5 text-sm" style={{ ...cardStyle, color: 'var(--text-primary)' }} />
                        </div>
                        <div className="rounded-lg overflow-y-auto" style={{ maxHeight: 180, border: '1px solid var(--border-default)' }}>
                            {nacitam ? (
                                <div className="flex items-center justify-center py-6"><Loader2 size={16} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} /></div>
                            ) : filtrovaniDod.length === 0 ? (
                                <div className="py-6 text-center text-xs" style={{ color: 'var(--text-tertiary)' }}>Žádní dodavatelé</div>
                            ) : filtrovaniDod.map(d => {
                                const on = vybraniDod.has(d.id);
                                return (
                                    <label key={d.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm" style={{ color: 'var(--text-primary)' }}>
                                        <input type="checkbox" checked={on} onChange={() => toggleDod(d.id)} style={{ accentColor: SECTION_COLOR }} />
                                        <span className="truncate">{d.nazev}</span>
                                        {d.ico && <span className="ml-auto text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{d.ico}</span>}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Patička */}
                <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t" style={{ borderColor: 'var(--border)' }}>
                    <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>Prázdný výběr = vše</span>
                    <button onClick={generovat} disabled={!platne}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                        style={{ background: SECTION_COLOR }}>
                        <FileText size={16} /> Generovat PDF
                    </button>
                </div>
            </div>
        </div>
    );
}
